<template>
  <div class="min-h-screen bg-gray-100">
    <!-- Vista para usuarios autenticados -->
    <div v-if="user && !showPublicView">
      <SidebarMenu
        :user="user"
        :isMenuOpen="isMenuOpen"
        @toggle-menu="toggleMenu"
        @close-menu="closeMenu"
        @logout="logout"
      />
      
      <div class="md:ml-64">
        <main class="p-4">
          <div class="p-6">
            <ConstanciaContent 
              :constanciaData="constanciaData"
              :loading="loadingConstancia"
              :error="errorConstancia"
              :guid="currentGuid"
            />
          </div>
        </main>
      </div>
    </div>

    <!-- Vista para usuarios NO autenticados o vista pública con GUID -->
    <div v-else-if="!loading" class="min-h-screen">
      <header class="bg-gray-100">
        <div class="max-w-4xl mx-auto px-4 py-6">
          <div class="flex justify-between items-center">
            <div class="flex items-center space-x-3">
              <div class="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                </svg>
              </div>
              <h1 class="text-2xl font-bold text-gray-800">Servicios del Atlántico</h1>
            </div>
            <button
              @click="goToLogin"
              class="hidden px-4 py-2 bg-blue-500 text-white rounded-lg shadow hover:bg-blue-600 transition-colors"
            >
              <span>Iniciar Sesión</span>
            </button>
          </div>
        </div>
      </header>

      <main class="max-w-4xl mx-auto px-4 py-8">
        <ConstanciaContent 
          :constanciaData="constanciaData"
          :loading="loadingConstancia"
          :error="errorConstancia"
          :guid="currentGuid"
        />
      </main>

      <!-- Footer -->
      <footer class="mt-12">
        <div class="max-w-4xl mx-auto px-4 py-6">
          <div class="flex flex-col md:flex-row justify-between items-center">
            <div class="flex space-x-6 mt-4 md:mt-0">
              <a href="/terminos-del-servicio" class="text-gray-600 hover:text-gray-900 text-sm transition-colors">
                Términos del Servicio
              </a>
              <a href="/politica-de-privacidad" class="text-gray-600 hover:text-gray-900 text-sm transition-colors">
                Política de Privacidad
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>

    <!-- Loading state general -->
    <div v-if="loading" class="min-h-screen flex items-center justify-center bg-gray-100">
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
        <p class="text-gray-600">Cargando...</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import SidebarMenu from '@/components/SidebarMenu.vue';
import ConstanciaContent from '@/components/ConstanciaContent.vue';
import { getConstancia } from '@/services/api';

export default {
  components: {
    SidebarMenu,
    ConstanciaContent,
  },
  data() {
    return {
      user: null,
      isMenuOpen: false,
      loading: false,
      loadingConstancia: false,
      error: null,
      errorConstancia: null,
      constanciaData: null,
      showPublicView: true,
      currentGuid: '', // Almacenar el GUID actual
    };
  },
  async created() {
    // Primero cargar la constancia
    await this.loadConstancia();
    
    // Luego intentar verificar autenticación (sin bloquear)
    this.checkAuthStatus();
  },
  methods: {
    async checkAuthStatus() {
      try {
        const response = await axios.get('/me');
        this.user = response.data.user;
      } catch (error) {
        this.user = null;
      }
    },

    async loadConstancia() {
      const guid = this.extractGuidFromUrl();
      
      if (!guid) {
        this.errorConstancia = 'No se proporcionó un identificador de constancia válido. La URL debe contener el parámetro guid.';
        this.loadingConstancia = false;
        return;
      }

      // Guardar el GUID para pasarlo al componente hijo
      this.currentGuid = guid;

      this.loadingConstancia = true;
      this.errorConstancia = null;

      try {
        
        const response = await getConstancia({ guid });
        
        let constanciaInfo = null;
        
        // Intentar diferentes estructuras de respuesta
        if (response.data) {
          // Si response.data tiene idstudent directamente
          if (response.data.idstudent) {
            constanciaInfo = response.data;
          }
          // Si está dentro de response.data.data
          else if (response.data.data && response.data.data.idstudent) {
            constanciaInfo = response.data.data;
          }
          // Si está dentro de response.data.constancia
          else if (response.data.constancia && response.data.constancia.idstudent) {
            constanciaInfo = response.data.constancia;
          }
          // Si está dentro de response.data.document
          else if (response.data.document && response.data.document.idstudent) {
            constanciaInfo = response.data.document;
          }
          // Si es un array y tiene elementos
          else if (Array.isArray(response.data) && response.data.length > 0) {
            constanciaInfo = response.data[0];
          }
          // Si tiene una propiedad "success" o "status"
          else if (response.data.success !== undefined || response.data.status !== undefined) {
            // Buscar en todas las propiedades
            for (const key in response.data) {
              if (response.data[key] && typeof response.data[key] === 'object' && response.data[key].idstudent) {
                constanciaInfo = response.data[key];
                break;
              }
            }
          }
        }
        
        if (constanciaInfo && constanciaInfo.idstudent) {
          this.constanciaData = constanciaInfo;
        } else {
          this.errorConstancia = 'La URL no contiene la información esperada. Verifique que esté consultando la constancia correctamente.';
        }
      } catch (error) {
        
        if (error.response?.status === 404) {
          this.errorConstancia = 'La constancia solicitada no existe o ha expirado.';
        } else if (error.response?.status === 401 || error.response?.status === 403) {
          this.errorConstancia = 'No tiene permisos para ver esta constancia.';
        } else if (error.response?.status === 422) {
          this.errorConstancia = 'El identificador de constancia no es válido.';
        } else {
          this.errorConstancia = 'Error al cargar la constancia. Por favor, verifique la URL e intente nuevamente.';
        }
      } finally {
        this.loadingConstancia = false;
      }
    },

    extractGuidFromUrl() {
      const fullPath = window.location.pathname + window.location.search;
      
      // Intentar desde query params de URL
      const urlParams = new URLSearchParams(window.location.search);
      const guidFromQuery = urlParams.get('guid');
      if (guidFromQuery) {
        return guidFromQuery;
      }
      
      // Intentar desde $route.query
      if (this.$route.query && this.$route.query.guid) {
        return this.$route.query.guid;
      }
      
      // Intentar extraer desde el path completo
      const pathMatch = fullPath.match(/guid=([a-zA-Z0-9-]+)/);
      if (pathMatch && pathMatch[1]) {
        return pathMatch[1];
      }
      
      // Intentar desde el path del route
      if (this.$route.path && this.$route.path.includes('guid=')) {
        const routeMatch = this.$route.path.match(/guid=([a-zA-Z0-9-]+)/);
        if (routeMatch && routeMatch[1]) {
          return routeMatch[1];
        }
      }
      
      // Intentar desde params.pathMatch (Vue Router 4)
      if (this.$route.params && this.$route.params.pathMatch) {
        const pathMatchParam = Array.isArray(this.$route.params.pathMatch) 
          ? this.$route.params.pathMatch.join('/') 
          : this.$route.params.pathMatch;
        
        const guidMatch = pathMatchParam.match(/guid=([a-zA-Z0-9-]+)/);
        if (guidMatch && guidMatch[1]) {
          return guidMatch[1];
        }
      }
      return null;
    },

    goToLogin() {
      this.$router.push('/login');
    },

    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },

    closeMenu() {
      this.isMenuOpen = false;
    },

    async logout() {
      try {
        await axios.get('/sanctum/csrf-cookie');
        await new Promise(resolve => setTimeout(resolve, 100));

        const response = await axios.post('/logout', {}, {
          withCredentials: true,
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        
        console.log(response);
        
        document.cookie.split(";").forEach(function(c) { 
          document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });
        
        this.$router.push('/login');
      } catch (error) {
        console.error('Error al cerrar sesión:', error.response?.data || error);
      }
    }
  }
};
</script>